import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SafeAreaView, StyleSheet, Text, View } from '@/ui';
import { useRouter } from '@/navigation/router';
import { BrandButton } from '@/components/ui/BrandButton';
import { useAppTheme, type ThemeColors } from '@/theme/ThemeProvider';

/**
 * No native counterpart — a phone app cannot be handed a URL that does not
 * exist, and a browser can. A typo'd or expired link lands here rather than on
 * a blank shell.
 */
export default function NotFound() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { colors: C } = useAppTheme();
  const styles = useMemo(() => createStyles(C), [C]);

  return (
    <View style={styles.bg}>
      <SafeAreaView style={styles.safe}>
        <View style={styles.body}>
          <Text style={styles.code}>404</Text>
          <Text style={styles.title}>{t('notFoundTitle', 'This page has moved on')}</Text>
          <Text style={styles.subtitle}>
            {t('notFoundBody', 'The link you followed does not lead anywhere in the app.')}
          </Text>
          <View style={styles.action}>
            <BrandButton variant="primary" onPress={() => router.replace('/home')}>
              {t('notFoundAction', 'Back to home')}
            </BrandButton>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
}

function createStyles(C: ThemeColors) {
  return StyleSheet.create({
    bg: { flex: 1, backgroundColor: C.appBg },
    safe: { flex: 1 },
    body: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 32, gap: 8 },
    code: { fontFamily: 'Marcellus', fontSize: 56, color: C.accentLine },
    title: { fontFamily: 'Marcellus', fontSize: 22, color: C.text, textAlign: 'center' },
    subtitle: { fontSize: 13.5, color: C.textDim, textAlign: 'center', lineHeight: 19 },
    action: { alignSelf: 'stretch', marginTop: 22 },
  });
}
